import { apiClient, ApiError, getLastApiSuccessAt, noteApiActivity, onApiSuccess } from "@nabble/sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { createSessionMonitor, type SessionMonitor } from "../model/sessionMonitor";
import { type PingResult, type SessionState, timingForIdleMinutes } from "../model/sessionTiming";

const SHARED_KEY = "nabble.lastApiAt";
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;

/** GET /api/me (not under /api/auth, which is rate limited) renews the session. */
async function pingSession(): Promise<PingResult> {
  try {
    await apiClient("/api/me");
    return "alive";
  } catch (err) {
    // A 401 also fires the apiClient's global handler, which sends the user
    // to /login with the "session expired" notice.
    if (err instanceof ApiError && err.status === 401) return "expired";
    return "unknown";
  }
}

export interface SessionKeepAlive extends SessionState {
  stayConnected: () => Promise<PingResult>;
}

/**
 * Keeps an active user's session alive and exposes the warning phase. Also
 * shares the last-activity timestamp across tabs of the same browser so one
 * busy tab keeps the others from warning.
 */
export function useSessionKeepAlive(idleMinutes: number, enabled = true): SessionKeepAlive {
  const [state, setState] = useState<SessionState>({ phase: "active", expiresAt: 0 });
  const monitorRef = useRef<SessionMonitor | null>(null);

  useEffect(() => {
    if (!enabled) return;

    try {
      const shared = Number(window.localStorage.getItem(SHARED_KEY));
      if (Number.isFinite(shared) && shared > 0 && shared <= Date.now()) noteApiActivity(shared);
    } catch {
      // ignore: storage unavailable
    }
    if (getLastApiSuccessAt() === 0) noteApiActivity(Date.now());

    const monitor = createSessionMonitor({
      config: timingForIdleMinutes(idleMinutes),
      getLastActivityAt: getLastApiSuccessAt,
      ping: pingSession,
      onChange: setState,
    });
    monitorRef.current = monitor;
    monitor.start();

    const offSuccess = onApiSuccess((at) => {
      try {
        window.localStorage.setItem(SHARED_KEY, String(at));
      } catch {
        // ignore
      }
      monitor.evaluate();
    });

    function onStorage(e: StorageEvent): void {
      if (e.key !== SHARED_KEY || !e.newValue) return;
      const at = Number(e.newValue);
      if (Number.isFinite(at) && at <= Date.now() + 5000) noteApiActivity(at);
    }

    let lastActivity = 0;
    function onActivity(): void {
      const t = Date.now();
      if (t - lastActivity < 5000) return;
      lastActivity = t;
      monitor.activity();
    }

    function onVisibility(): void {
      if (document.visibilityState !== "visible") return;
      monitor.evaluate();
      onActivity();
    }

    for (const type of ACTIVITY_EVENTS) document.addEventListener(type, onActivity, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);

    return () => {
      monitor.stop();
      monitorRef.current = null;
      offSuccess();
      for (const type of ACTIVITY_EVENTS) document.removeEventListener(type, onActivity, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, [idleMinutes, enabled]);

  const stayConnected = useCallback(async (): Promise<PingResult> => {
    return monitorRef.current ? monitorRef.current.stayConnected() : "unknown";
  }, []);

  return { ...state, stayConnected };
}
